/**
 * src/components/admin/AdminBusinesses.js
 *
 * Tab de gestión de negocios en el panel admin.
 * Recibe `businesses` (array reactivo desde AdminPage) y callbacks
 * para modificar el estado centralizado.
 */

import { define } from '@components/Component.js'
import { h } from '@core/dom.js'
import {
    getBusinessStatus, STATUS_LABELS, showToast, fmtDate, toInputDate, nextId
} from './adminHelpers.js'
import { uploadButton } from './adminUpload.js'
import { directoryData } from '@/db/directory.db.js'

const CATEGORIES = directoryData.categories.map(c => ({ name: c.name, slug: c.slug }))

/* ── Formulario de negocio (modal) ───────────────────────── */
function buildForm(business = {}) {
    const isEdit = !!business.id
    const def = {
        id: null, plan: 'plata', name: '', category: '', categorySlug: '',
        description: '', fullDescription: '', logo: '', image: '',
        video: '', gallery: [], catalog: { url: '', label: '', description: '' },
        location: '', mapLabel: '', mapUrl: '', mapEmbed: '',
        whatsapp: '', social: { instagram: '', facebook: '', tiktok: '', website: '' },
        rating: '', reviews: '', badge: '', badgeColor: '',
        open: true, hasPromo: false, tags: [],
        enabled: true, activeFrom: '', activeTo: '',
        ...business,
        social:   { instagram: '', facebook: '', tiktok: '', website: '', ...(business.social   || {}) },
        catalog:  { url: '', label: '', description: '', ...(business.catalog  || {}) },
    }

    const f = (tag, attrs, ...ch) => h(tag, attrs, ...ch)

    const field = (label, input, hint) => f('div', { className: 'adm-field' },
        f('label', { className: 'adm-label' }, label),
        input,
        ...(hint ? [f('p', { className: 'adm-hint' }, hint)] : [])
    )
    const inp = (name, opts = {}) => f('input', {
        className: 'adm-input', name,
        value: opts.value ?? def[name] ?? '',
        type: opts.type ?? 'text',
        placeholder: opts.placeholder ?? ''
    })
    const sel = (name, options, value) => {
        const s = f('select', { className: 'adm-select', name })
        options.forEach(([v, l]) => {
            const o = f('option', { value: v }, l)
            if (v === value) o.selected = true
            s.appendChild(o)
        })
        return s
    }
    const textarea = (name, value, rows = 3) => f('textarea', {
        className: 'adm-textarea', name, rows
    }, value || def[name] || '')

    const sectionLabel = (txt) => f('div', { className: 'adm-section-label' }, txt)

    const form = f('form', { className: 'adm-form', id: 'biz-form' },
        /* Plan y estado */
        f('div', { className: 'adm-form-grid' },
            field('Nombre del negocio', inp('name', { placeholder: 'Ej: Restaurante El Sabor' })),
            field('Responsable del anuncio', inp('responsable', { placeholder: 'Ej: María Pérez', value: def.responsable || '' }),
                'Solo visible en este panel — la persona encargada del anuncio'),
            field('Plan', sel('plan', [['oro','⭐ Plan Oro'],['plata','🥈 Plan Plata']], def.plan)),
            field('Categoría', (() => {
                const catSel = f('select', { className: 'adm-select', name: 'category' })
                const hiddenSlug = f('input', { type: 'hidden', name: 'categorySlug', value: def.categorySlug || '' })
                const placeholder = f('option', { value: '' }, '— Selecciona una categoría —')
                placeholder.disabled = true
                if (!def.categorySlug) placeholder.selected = true
                catSel.appendChild(placeholder)
                CATEGORIES.forEach(cat => {
                    const o = f('option', { value: cat.name }, cat.name)
                    o.dataset.slug = cat.slug
                    if (cat.slug === def.categorySlug) { o.selected = true }
                    catSel.appendChild(o)
                })
                catSel.addEventListener('change', () => {
                    const opt = catSel.selectedOptions[0]
                    hiddenSlug.value = opt ? (opt.dataset.slug || '') : ''
                })
                const wrap = f('span')
                wrap.appendChild(catSel)
                wrap.appendChild(hiddenSlug)
                return wrap
            })()),
        ),
        field('Descripción corta', inp('description', { placeholder: 'Una línea para las tarjetas del directorio', value: def.description })),
        field('Descripción completa', textarea('fullDescription', def.fullDescription, 4)),

        /* Media */
        f('div', { className: 'adm-form-grid' },
            sectionLabel('📸 Imágenes y multimedia'),
            field('Logo', uploadButton(inp('logo', { placeholder: 'URL o clic en 📤 Subir', value: def.logo })),
                'Clic en 📤 para subir la imagen desde tu computador'),
            field('Imagen principal', uploadButton(inp('image', { placeholder: 'URL o clic en 📤 Subir', value: def.image }))),
            field('URL Video (YouTube embed)', inp('video', { placeholder: 'https://www.youtube.com/embed/ID', value: def.video }),
                'El video sí va en YouTube: cambia "watch?v=" por "embed/"'),
        ),
        field('Galería (máx 10 Plan Oro / 5 Plan Plata)',
            uploadButton(
                f('textarea', {
                    className: 'adm-textarea', name: 'gallery', rows: 5
                }, (def.gallery || []).join('\n')),
                { multiple: true }
            ),
            'Clic en 📤 y selecciona varias fotos a la vez. Cada URL queda en su línea.'
        ),

        /* Catálogo PDF */
        f('div', { className: 'adm-form-grid' },
            sectionLabel('📄 Catálogo PDF (Plan Oro)'),
            field('PDF del catálogo', uploadButton(
                inp('catalogUrl', { placeholder: 'URL o clic en 📤 Subir', value: def.catalog.url }),
                { accept: 'application/pdf' }
            ), 'Clic en 📤 para subir el PDF desde tu computador'),
            field('Etiqueta', inp('catalogLabel', { placeholder: 'Ej: Ver catálogo completo', value: def.catalog.label })),
            field('Descripción', inp('catalogDescription', { placeholder: 'Ej: Todos los productos disponibles', value: def.catalog.description })),
        ),

        /* Ubicación */
        f('div', { className: 'adm-form-grid' },
            sectionLabel('📍 Ubicación'),
            field('Dirección', inp('location', { placeholder: 'Ej: Cra. 7 #10-15, Fusagasugá', value: def.location })),
            field('Etiqueta del mapa', inp('mapLabel', { placeholder: 'Ej: Centro Comercial Manila, local 5', value: def.mapLabel })),
            field('URL Google Maps (opcional)', inp('mapUrl', { placeholder: 'https://maps.app.goo.gl/…', value: def.mapUrl }),
                'Google Maps → Compartir → Copiar enlace. Vacío = se genera desde la dirección'),
            field('Embed del mapa (opcional)', inp('mapEmbed', { placeholder: 'Se genera solo desde la dirección si lo dejas vacío', value: def.mapEmbed }),
                'Déjalo vacío y el mapa se crea automáticamente con la dirección'),
        ),

        /* Contacto */
        f('div', { className: 'adm-form-grid' },
            sectionLabel('📞 Contacto y redes'),
            field('WhatsApp (con código país)', inp('whatsapp', { placeholder: '573124288323', value: def.whatsapp })),
            field('Instagram', inp('instagram', { placeholder: 'https://instagram.com/…', value: def.social.instagram || '' })),
            field('Facebook', inp('facebook', { placeholder: 'https://facebook.com/…', value: def.social.facebook  || '' })),
            field('TikTok', inp('tiktok', { placeholder: 'https://tiktok.com/@…', value: def.social.tiktok    || '' })),
            field('Sitio web', inp('website', { placeholder: 'https://minegocio.com', value: def.social.website   || '' })),
        ),

        /* Métricas */
        f('div', { className: 'adm-form-grid adm-form-grid--3' },
            sectionLabel('⭐ Métricas y etiquetas'),
            field('Rating (0-5)', inp('rating', { type: 'number', placeholder: '4.8', value: def.rating })),
            field('Nº de reseñas', inp('reviews', { type: 'number', placeholder: '128', value: def.reviews })),
            field('Badge texto', inp('badge', { placeholder: 'Ej: Nuevo', value: def.badge || '' })),
            field('Color badge', sel('badgeColor', [
                ['','Sin color'],['green','Verde'],['gold','Dorado'],
                ['blue','Azul'],['purple','Morado'],['teal','Teal'],['orange','Naranja']
            ], def.badgeColor || '')),
            field('Tags (separados por coma)', inp('tags', { placeholder: 'Restaurante, Italiano, Café', value: (def.tags || []).join(', ') })),
        ),

        /* Flags */
        f('div', { className: 'adm-form-grid adm-form-grid--3' },
            field('¿Está abierto?', sel('open', [['true','Sí, abierto'],['false','No, cerrado']], String(def.open))),
            field('¿Tiene promo activa?', sel('hasPromo', [['true','Sí'],['false','No']], String(def.hasPromo))),
        ),

        /* Programación */
        f('div', { className: 'adm-schedule' },
            f('div', { className: 'adm-schedule-title' }, '🗓 Programación de visibilidad'),
            f('div', { className: 'adm-form-grid adm-form-grid--3' },
                field('Habilitado', sel('enabled', [['true','✅ Habilitado'],['false','❌ Deshabilitado']], String(def.enabled))),
                field('Visible desde (opcional)', inp('activeFrom', { type: 'date', value: toInputDate(def.activeFrom) }),
                    'Déjalo vacío si no hay fecha de inicio'),
                field('Visible hasta (opcional)', inp('activeTo', { type: 'date', value: toInputDate(def.activeTo) }),
                    'Déjalo vacío si no hay fecha de fin'),
            )
        ),
    )

    return form
}

/** Genera la URL de embed de Google Maps a partir de una dirección */
function buildMapEmbed(address) {
    if (!address) return ''
    const query = encodeURIComponent(`${address}, Fusagasugá, Cundinamarca`)
    return `https://maps.google.com/maps?q=${query}&output=embed`
}

/** Genera el enlace "Cómo llegar" de Google Maps a partir de una dirección */
function buildMapUrl(address) {
    if (!address) return ''
    const query = encodeURIComponent(`${address}, Fusagasugá, Cundinamarca`)
    return `https://www.google.com/maps/search/${query}`
}

/** Extrae los valores del formulario como objeto business */
function readForm(form) {
    const g = name => form.querySelector(`[name="${name}"]`)?.value?.trim() ?? ''
    return {
        name:            g('name'),
        responsable:     g('responsable') || null,
        plan:            g('plan'),
        category:        g('category'),
        categorySlug:    g('categorySlug'),
        description:     g('description'),
        fullDescription: form.querySelector('[name=fullDescription]')?.value?.trim() ?? '',
        logo:            g('logo'),
        image:           g('image'),
        video:           g('video'),
        gallery:         (form.querySelector('[name=gallery]')?.value ?? '')
                            .split('\n').map(s => s.trim()).filter(Boolean).slice(0, 10),
        catalog: {
            url:         g('catalogUrl'),
            label:       g('catalogLabel'),
            description: g('catalogDescription'),
        },
        location:  g('location'),
        mapLabel:  g('mapLabel'),
        // Si los campos de mapa se dejan vacíos, se generan desde la dirección
        mapUrl:    g('mapUrl')   || buildMapUrl(g('mapLabel') || g('location')),
        mapEmbed:  g('mapEmbed') || buildMapEmbed(g('mapLabel') || g('location')),
        whatsapp:  g('whatsapp'),
        social: {
            instagram: g('instagram') || null,
            facebook:  g('facebook')  || null,
            tiktok:    g('tiktok')    || null,
            website:   g('website')   || null,
        },
        rating:     Number(g('rating'))  || 0,
        reviews:    Number(g('reviews')) || 0,
        badge:      g('badge') || null,
        badgeColor: g('badgeColor') || null,
        open:       g('open')    === 'true',
        hasPromo:   g('hasPromo') === 'true',
        tags:       g('tags').split(',').map(t => t.trim()).filter(Boolean),
        enabled:    g('enabled') === 'true',
        activeFrom: g('activeFrom') || null,
        activeTo:   g('activeTo')   || null,
    }
}

/* ── Componente principal ────────────────────────────────── */
define('AdminBusinesses', ({ businesses, onSave, onDelete, onToggle }) => {

    /** Abre un modal con el formulario de crear/editar */
    function openModal(bizData) {
        const isEdit = !!bizData

        // Guardamos referencia directa al formulario para no depender de querySelector('#id')
        const form = buildForm(bizData || {})
        form.addEventListener('submit', e => e.preventDefault())

        const closeBtn  = h('button', { className: 'adm-btn adm-btn--ghost adm-btn--sm adm-btn--icon', type: 'button' }, '✕')
        const cancelBtn = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, 'Cancelar')
        const saveBtn   = h('button', { className: 'adm-btn adm-btn--primary', type: 'button' },
            isEdit ? '💾 Guardar cambios' : '✅ Crear negocio'
        )

        const overlay = h('div', { className: 'adm-modal-overlay' },
            h('div', { className: 'adm-modal' },
                h('div', { className: 'adm-modal__head' },
                    h('span', { className: 'adm-modal__title' },
                        isEdit ? `✏️ Editar: ${bizData.name}` : '➕ Nuevo negocio'
                    ),
                    closeBtn
                ),
                h('div', { className: 'adm-modal__body' }, form),
                h('div', { className: 'adm-modal__foot' }, cancelBtn, saveBtn)
            )
        )

        const close = () => overlay.remove()

        closeBtn.addEventListener('click',  close)
        cancelBtn.addEventListener('click', close)
        overlay.addEventListener('click', e => { if (e.target === overlay) close() })

        saveBtn.addEventListener('click', () => {
            try {
                const data = readForm(form)
                if (!data.name) { showToast('⚠️ El nombre es obligatorio'); return }
                if (isEdit) {
                    onSave({ ...bizData, ...data })
                    showToast(`✅ "${data.name}" actualizado`)
                } else {
                    onSave({ id: nextId(businesses), ...data })
                    showToast(`✅ "${data.name}" creado`)
                }
                close()
            } catch (err) {
                showToast(`❌ Error al guardar: ${err.message}`, 5000)
                console.error('[AdminBusinesses] saveBtn error:', err)
            }
        })

        document.body.appendChild(overlay)
    }

    /** Fila de la tabla */
    function buildRow(b) {
        const status = getBusinessStatus(b)

        const statusEl = h('span', { className: `adm-status adm-status--${status}` },
            h('span', { className: 'adm-status__dot' }),
            STATUS_LABELS[status].replace('● ', '')
        )

        const toggle = h('label', { className: 'adm-toggle' },
            h('input', { type: 'checkbox', checked: b.enabled }),
            h('span',  { className: 'adm-toggle__slider' })
        )
        toggle.querySelector('input').addEventListener('change', e => {
            onToggle(b.id, e.target.checked)
        })

        const datesEl = h('div', { className: 'adm-dates' })
        if (b.activeFrom || b.activeTo) {
            if (b.activeFrom) datesEl.appendChild(h('span', {}, `Desde: ${fmtDate(b.activeFrom)}`))
            if (b.activeTo)   datesEl.appendChild(h('span', {}, `Hasta: ${fmtDate(b.activeTo)}`))
        } else {
            datesEl.appendChild(h('span', {}, 'Sin límite'))
        }

        const editBtn = h('button', { className: 'adm-btn adm-btn--ghost adm-btn--sm', type: 'button' }, '✏️ Editar')
        const delBtn  = h('button', { className: 'adm-btn adm-btn--danger adm-btn--sm', type: 'button' }, '🗑')

        editBtn.addEventListener('click', () => openModal(b))
        delBtn.addEventListener('click', () => {
            if (confirm(`¿Eliminar "${b.name}"? Esta acción no se puede deshacer.`)) {
                onDelete(b.id)
                showToast(`🗑 "${b.name}" eliminado`)
            }
        })

        return h('tr', {},
            h('td', {}, h('span', { className: 'adm-id' }, `#${b.id}`)),
            h('td', {}, h('strong', {}, b.name)),
            h('td', {}, b.responsable || '—'),
            h('td', {}, h('span', { className: `adm-badge adm-badge--${b.plan}` }, b.plan === 'oro' ? '⭐ Oro' : '🥈 Plata')),
            h('td', {}, b.category || '—'),
            h('td', {}, statusEl),
            h('td', {}, toggle),
            h('td', {}, datesEl),
            h('td', {}, h('div', { className: 'adm-row-actions' }, editBtn, delBtn))
        )
    }

    /* ── Render del tab ──────────────────────────────────── */
    const addBtn = h('button', { className: 'adm-btn adm-btn--primary', type: 'button' },
        '➕ Nuevo negocio'
    )
    addBtn.addEventListener('click', () => openModal(null))

    const tbody = h('tbody', {})

    const rebuild = () => {
        tbody.innerHTML = ''
        if (!businesses.length) {
            tbody.appendChild(
                h('tr', {},
                    h('td', { colSpan: 9 },
                        h('div', { className: 'adm-empty' },
                            h('div', { className: 'adm-empty__icon' }, '🏪'),
                            h('div', { className: 'adm-empty__text' }, 'No hay negocios registrados')
                        )
                    )
                )
            )
        } else {
            businesses.forEach(b => tbody.appendChild(buildRow(b)))
        }
    }

    rebuild()

    return h('div', {},
        h('div', { className: 'admin-panel' },
            h('div', { className: 'admin-panel__head' },
                h('div', {},
                    h('span', { className: 'admin-panel__title' }, '🏪 Negocios'),
                    h('span', { className: 'admin-panel__count' }, `${businesses.length} total`)
                ),
                addBtn
            ),
            h('div', { style: 'overflow-x:auto' },
                h('table', { className: 'admin-table' },
                    h('thead', {},
                        h('tr', {},
                            h('th', {}, 'ID'),
                            h('th', {}, 'Nombre'),
                            h('th', {}, 'Responsable'),
                            h('th', {}, 'Plan'),
                            h('th', {}, 'Categoría'),
                            h('th', {}, 'Estado'),
                            h('th', {}, 'Habilitado'),
                            h('th', {}, 'Programación'),
                            h('th', {}, 'Acciones'),
                        )
                    ),
                    tbody
                )
            )
        )
    )
})
